
import { Component, input, model, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { getCachedValue, LocalStorageKeys, removeCachedValue, setCachedValue } from "@shared-utils";
import { Visits } from "@types-lib";
import { debounceTime } from "rxjs";

@Component({
    selector: 'lib-table-pagination',
    imports: [FormsModule, ReactiveFormsModule],
    templateUrl: './table-pagination.component.html',
    styleUrl: './table-pagination.component.scss'
})
export class TablePaginationComponent implements OnInit {
	private fb = inject(FormBuilder);

	itemsPerPage = 10;
	savePageLocalStorageKey = input<LocalStorageKeys>();
	pagination = model<Visits.BasePagination | undefined>();
	itemsPerPageOptions = input<string | number[]>();
	protected readonly localStorage = localStorage;

	paginationForm: FormGroup;
	totalNumberOfPages: number[] = [];
	previousValue: Visits.TablePaginationFormValue = {
		limit: 100,
		offset: 0,
		currentPageNumber: 1,
		total: 9999,
		saveOnChange: true,
	};

	constructor() {
		this.paginationForm = this.initializeForm();
		this.subscribeToFormChanges();
	}

	ngOnInit() {
		const key = this.savePageLocalStorageKey()
		if (key) {
			const cachedValue = getCachedValue(key)
			if (cachedValue) {
				this.pagination.set(cachedValue)
			}
		}
		this.updatePaginationForm();
	}

	// Initialize Form
	private initializeForm(): FormGroup {
		return this.fb.group({
			limit: new FormControl(),
			offset: new FormControl(),
			currentPageNumber: new FormControl(1),
			total: new FormControl(),
			saveOnChange: new FormControl(true),
		});
	}

	// Subscribe to Form Changes with Debounce
	private subscribeToFormChanges() {
		this.paginationForm.valueChanges
			.pipe(debounceTime(500))
			.subscribe((value: Visits.TablePaginationFormValue) => this.handleFormChanges(value));
	}

	// Update Form from Pagination Input
	private updatePaginationForm() {
		if (this.pagination()) {
			this.totalNumberOfPages = this.calculateTotalPages(this.pagination()?.total, this.itemsPerPage);
			const { limit, offset, total } = this.pagination() as Visits.BasePagination;
			const currentPageNumber = Math.floor(offset / limit) + 1;

			this.paginationForm.patchValue({ limit, offset, total, currentPageNumber, saveOnChange: this.pagination()?.saveOnChange });
			this.previousValue = { ...this.paginationForm.value };
		}
	}

	// Helper method to ensure the value is a number
	private convertToNumber(value: any): number {
		return isNaN(Number(value)) ? 0 : Number(value);
	}

	// Calculate Total Pages Array
	private calculateTotalPages(total: number | undefined, itemsPerPage: number): number[] {
		return total ? Array.from({ length: Math.ceil(total / itemsPerPage) }, (_, i) => i + 1) : [1];
	}

	// Update Local Storage
	private updateLocalStorageCache(pagination: Visits.BasePagination) {
		const {saveOnChange} = pagination
		const key = this.savePageLocalStorageKey()
		if (key) {
			if (saveOnChange) {
				setCachedValue(key, pagination);
			} else {
				removeCachedValue(key)
			}
		}
	}

	// Navigation Handlers
	goPrevious() {
		let { currentPageNumber } = this.paginationForm.value;
		if (currentPageNumber > 1) {
			currentPageNumber--;
			this.paginationForm.patchValue({ currentPageNumber });
		}
	}

	goNext() {
		let { currentPageNumber } = this.paginationForm.value;
		if (currentPageNumber < this.totalNumberOfPages.length) {
			currentPageNumber++;
			this.paginationForm.patchValue({ currentPageNumber });
		}
	}

	// Handle Form Changes
	handleFormChanges(value: Visits.TablePaginationFormValue) {
		const updatedPagination: Visits.BasePagination = {
			limit: value.limit,
			offset: (value.currentPageNumber * value.limit) - value.limit,  // Calculate offset
			total: value.total,
			saveOnChange: value.saveOnChange,
		};

		const hasChanges = Object.keys(value).some((key) => value[key as keyof Visits.TablePaginationFormValue] !== this.previousValue[key as keyof Visits.TablePaginationFormValue]);

		if (hasChanges) {
			this.previousValue = { ...value };

			// Ensure limit, offset, and total are numbers
			updatedPagination.limit = this.convertToNumber(updatedPagination.limit);
			updatedPagination.offset = this.convertToNumber(updatedPagination.offset);
			updatedPagination.total = this.convertToNumber(updatedPagination.total);

			this.pagination.set(updatedPagination);
			this.updateLocalStorageCache(updatedPagination);
		}
	}
}
